import { Injectable, OnDestroy } from "@angular/core";
import { ActivatedRoute, ActivationEnd, Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { combineLatest, lastValueFrom, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CityDto, ContractDto, HospitalizationDto, InsurerDto, KioskMaintenanceDto, ProvinceDto, RefLanguageDto, SiteDto } from "../api/models";
import { TokenResponseModel } from "../authenticator/models";
import { DobTypeEnum } from "../shared/enums/dobType.enum";
import { AppState } from "../state/app.state";
import { getFullTokenLoaded, getToken } from "./components/authentication/state/authentication.selectors";
import { getShowErrorPage } from "./components/error/state/error.selectors";
import { getMaintenance } from "./components/maintenance/state/maintenance.selectors";
import { setStepperIndex } from "./components/master/stepper/state/stepper.actions";
import { getStepperIndex } from "./components/master/stepper/state/stepper.selector";
import { getShowScreenSaver, getSelectedLanguage, getCardScanBypassed, getSelectedInsurer, getCardIdShowPage, getInsurerHasActiveQuestionnaire, getBirthdate, getOverwritePreHospiConfirmed, getAdmissionDate, getSelectedProvince, getSelectedCity, getSelectedSite, getConfirmedSite, getQuestionCompleted, getQuestionConfirmed, getSelectedContract, getStartupProcessDone, getCardId, getContractHasLanguageSupported, getAgreementChecked, getHospitalization } from "./components/transaction-info/state/transaction-info.selectors";
import { getWebsiteStarted } from "./state/kiosk.selectors";

@Injectable({
	providedIn: "root",
})
export class ViewLocator implements OnDestroy{
	currentStepperIndex: number = undefined;
	showErrorPage: boolean = undefined;
	showScreenSaver: boolean = undefined;
	selectedLanguage: RefLanguageDto = undefined;
	cardScanBypassed: boolean = undefined;
	selectedInsurer: InsurerDto = undefined;
	cardIdShowPage: boolean = undefined;
	insurerHasActiveQuestionnaire: boolean = undefined;
	birthdate: string = undefined;
	overwritePreHospiConfirmed = false;
	fullTokenLoaded = false;
	agreementChecked = false;
	admissionDate: Date = undefined;
	provinceSelected: ProvinceDto = undefined;
	citySelected: CityDto = undefined;
	hospitalSelected: SiteDto = undefined;
	hospitalConfirmed: boolean = undefined;
	questionCompleted: boolean = undefined;
	questionConfirmed: boolean = undefined;
	token: TokenResponseModel = undefined;
	contract: ContractDto = undefined;
	dobType: DobTypeEnum = undefined;
	maintenance: KioskMaintenanceDto = undefined;
	getStartupProcessDone: boolean = undefined;
	websiteStarted = false;
	cardId = "";
	contractHasLanguageSupported: boolean = undefined;
	hospitalization: HospitalizationDto = undefined;

	currentPage: string | undefined;
	nbSameRedirect = 1;
	private subs: Subscription[] = [];

	
	constructor(private store: Store<AppState>, private router: Router, private activateRoute: ActivatedRoute) {
		this.init();
		this.subs.push(
			combineLatest([
				this.store.pipe(select(getStepperIndex)),
				this.store.pipe(select(getWebsiteStarted)),
				this.store.pipe(select(getShowErrorPage)),
				this.store.pipe(select(getShowScreenSaver)),
				this.store.pipe(select(getSelectedLanguage)),
				this.store.pipe(select(getCardScanBypassed)),
				this.store.pipe(select(getSelectedInsurer)),
				this.store.pipe(select(getCardIdShowPage)),
				this.store.pipe(select(getInsurerHasActiveQuestionnaire)),
				this.store.pipe(select(getBirthdate)),
				this.store.pipe(select(getOverwritePreHospiConfirmed)),
				this.store.pipe(select(getFullTokenLoaded)),
				this.store.pipe(select(getAgreementChecked)),
				this.store.pipe(select(getAdmissionDate)),
				this.store.pipe(select(getSelectedProvince)),
				this.store.pipe(select(getSelectedCity)),
				this.store.pipe(select(getSelectedSite)),
				this.store.pipe(select(getConfirmedSite)),
				this.store.pipe(select(getQuestionCompleted)),
				this.store.pipe(select(getQuestionConfirmed)),
				this.store.pipe(select(getToken)),
				this.store.pipe(select(getSelectedContract)),
				this.store.pipe(select(getMaintenance)),
				this.store.pipe(select(getStartupProcessDone)),
				this.store.pipe(select(getCardId)),
				this.store.pipe(select(getContractHasLanguageSupported)),
				this.store.pipe(select(getHospitalization)),

			])
			.pipe(distinctUntilChanged())
			.subscribe(([currentStepperIndex, webSiteStarted, showErrorPage, showScreenSaver, selectedLanguage, cardScanBypassed, selectedInsurer, cardIdShowPage, insurerHasActiveQuestionnaire, birthdate, overwritePreHospiConfirmed, fullTokenLoaded, agreementChecked, admissionDate, selectedProvince, selectedCity, selectedHospital, confirmedHospital, questionCompleted, questionConfirmed, token, contract, maintenance,
				getStartupProcessDone, getCardId, getContractHasLanguageSupported, getHospitalization]) => {
				this.currentStepperIndex = currentStepperIndex as number;
				this.websiteStarted = webSiteStarted as boolean;
				this.showErrorPage = showErrorPage as boolean;
				this.showScreenSaver = showScreenSaver as boolean;
				this.selectedLanguage = selectedLanguage as RefLanguageDto;
				this.cardScanBypassed = cardScanBypassed as boolean;
				this.selectedInsurer = selectedInsurer as InsurerDto;
				this.cardIdShowPage = cardIdShowPage as boolean;
				this.insurerHasActiveQuestionnaire = insurerHasActiveQuestionnaire as boolean;
				this.birthdate = birthdate as string;
				this.overwritePreHospiConfirmed = overwritePreHospiConfirmed as boolean;
				this.fullTokenLoaded = fullTokenLoaded as boolean;
				this.agreementChecked = agreementChecked as boolean;
				this.admissionDate = admissionDate as Date;
				this.provinceSelected = selectedProvince as ProvinceDto;
				this.citySelected = selectedCity as CityDto;
				this.hospitalSelected = selectedHospital as SiteDto;
				this.hospitalConfirmed = confirmedHospital as boolean;
				this.questionCompleted = questionCompleted as boolean;
				this.questionConfirmed = questionConfirmed as boolean;
				this.token = token as TokenResponseModel;
				this.contract = contract as ContractDto;
				this.maintenance = maintenance as KioskMaintenanceDto;
				this.getStartupProcessDone = getStartupProcessDone as boolean;
				this.cardId = getCardId as string;
				this.contractHasLanguageSupported = getContractHasLanguageSupported as unknown as boolean;
				this.hospitalization = getHospitalization as HospitalizationDto;

				this.checkPage();
			})
		);
	}
	
	init() {
		this.subs.push(this.router.events.pipe(map((val) => {
			if (val instanceof ActivationEnd && val.snapshot.url.length > 0) {
				this.currentPage = val.snapshot.url[0].path;
			}
		})).subscribe());
	}

	public checkPage() {
		this.subs.push(
			this.activateRoute.queryParams.subscribe(queryParams => {
				/*Conditions d'arrêt */
				if (this.showErrorPage) {
					this.redirect('error');
					return;
				}

				if (this.websiteStarted !== true)
					return;

				const userIsIdentified = (queryParams["ic"] || queryParams["setLanguage"] || queryParams["lang"] || queryParams["setInsurerId"] || queryParams["setCardNbr"]);
				if ((userIsIdentified || queryParams["MobileApp"] || queryParams["transacId"]) && this.getStartupProcessDone !== true) {
					return;
				}
				
				/*Navigation pour l'authentification, la maintenance, le screensaver */
				if (this.getStartupProcessDone != true) {
					this.redirect('app-login');
				}
				else if (this.showScreenSaver) {
					this.redirect('screen-saver');
				} else if (this.maintenance != undefined) {
					this.redirect('maintenance');
				} else if (this.selectedLanguage == undefined || this.selectedLanguage == null) {
					this.redirect('select-language', 1);
				} else if (this.contractHasLanguageSupported == false) {
					this.redirect('select-language', 1);
				} else if (environment.kioskMode && this.cardScanBypassed == undefined) {
					this.redirect('card-scan', 1);
				} else if (this.selectedInsurer == undefined) {
					this.redirect('select-insurer', 1);
				}
				else if (this.cardIdShowPage) { 
					this.redirect('enter-card-id', 1);
				} else if (this.birthdate === undefined && this.cardId && !this.contract) {
					this.redirect('enter-patient-dob', 1);
				} else {
					/*Navigation du process de déclaration */
					if (this.selectedInsurer && !this.selectedInsurer.questionnaireDeclarationsAllowed && this.selectedInsurer.callCenterDeclarationsAllowed
						&& this.hospitalization) {
						this.redirect('check-prehospi', 3);
					}
					else if (this.contract) {
						if (!this.agreementChecked) {
							this.redirect('agreement', 2);
						} else if (this.admissionDate == undefined) {
							this.redirect('enter-admission-date', 3);
						} else if (this.provinceSelected == undefined) {
							this.redirect('province', 3);
						} else if (this.citySelected == undefined) {
							this.redirect('city', 3);
						} else if (this.hospitalSelected == undefined) {
							this.redirect('hospital', 3);
						} else if (this.hospitalConfirmed === undefined || !this.hospitalConfirmed) {
							this.redirect('confirm-hospital', 3);
						} else if (this.overwritePreHospiConfirmed === undefined || !this.overwritePreHospiConfirmed) {
							this.redirect('check-prehospi', 3);
						} else if (this.questionCompleted === undefined || !this.questionCompleted) {
							this.redirect('question', 4);
						} else if (this.questionConfirmed === undefined || !this.questionConfirmed) {
							this.redirect('confirm-question', 4);
						} else {
							this.redirect('light', 5);
						}
					}
				}
			}
		));
	}
	private setCurrentStepperIndex(index: number) {
		if (this.currentStepperIndex != index)
			this.store.dispatch(setStepperIndex({ data: index }));
	}
	public redirect(page: string, stepperIndex?: number) {
		if (stepperIndex)
			this.setCurrentStepperIndex(stepperIndex);
		console.log('redirect to ' + page);

		if (this.currentPage == page) {
			this.nbSameRedirect++
			console.log('Same navigation to ' + page + ' : ' + this.nbSameRedirect+' => do nothing !');
		}
		else 
		{
			this.nbSameRedirect = 1;
			this.currentPage = page;
			this.router.navigate([page]);
		}
	}
	public ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}

}